import { getProductBrandLabel } from "@/lib/product-utils";

type JsonRecord = Record<string, unknown>;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_SUFFIX_REGEX =
  /(?:^|[-_])([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
export const PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE = "Unable to open product details";

function toNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeUuid(value: string): string | null {
  const normalized = value.trim().toLowerCase();
  if (!UUID_REGEX.test(normalized)) return null;
  return normalized;
}

export function slugifySegment(value: string | null | undefined, fallback: string): string {
  const source = toNonEmptyString(value) ?? fallback;
  const withoutDiacritics = source.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
  const slug = withoutDiacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");

  return slug || fallback;
}

export function buildProductSlugTail(params: {
  title: string | null | undefined;
  uuid: string;
}): string {
  const uuid = normalizeUuid(params.uuid) ?? params.uuid.trim().toLowerCase();
  const titleSlug = slugifySegment(params.title, "product");
  return `${titleSlug}-${uuid}`;
}

export function buildCanonicalProductPath(params: {
  brand: string | null | undefined;
  brandSlug?: string | null | undefined;
  title: string | null | undefined;
  uuid: string;
}): string {
  const brandSlug = toNonEmptyString(params.brandSlug) ?? slugifySegment(params.brand, "brand");
  const slugTail = buildProductSlugTail({ title: params.title, uuid: params.uuid });
  return `/product/${encodeURIComponent(brandSlug)}/${slugTail}`;
}

const PATH_UUID_SUFFIX_REGEX = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True when two product paths differ only in their trailing product id.
 *
 * Another id of the same product (a published sitemap id, a historical alias)
 * renders in place with a canonical link instead of a permanent redirect, so
 * no redirect between ids exists that a client could have cached the other way.
 */
export function productPathsDifferOnlyInId(requestedPath: string, canonicalPath: string): boolean {
  const requestedPrefix = requestedPath.replace(PATH_UUID_SUFFIX_REGEX, "");
  return (
    requestedPrefix !== requestedPath &&
    requestedPrefix === canonicalPath.replace(PATH_UUID_SUFFIX_REGEX, "")
  );
}

export function extractUuidFromProductSlugTail(slugTail: string): string | null {
  const normalized = slugTail.trim().toLowerCase();
  const suffixMatch = normalized.match(UUID_SUFFIX_REGEX);
  if (suffixMatch?.[1]) return normalizeUuid(suffixMatch[1]);
  return normalizeUuid(normalized);
}

export function getProductUuidFromRecord(
  product: JsonRecord,
  fallbackUuid?: string | null
): string | null {
  const uuid = toNonEmptyString(product.uuid);
  if (uuid) {
    const normalized = normalizeUuid(uuid);
    if (normalized) return normalized;
  }

  const idAsString =
    typeof product.id === "number"
      ? String(product.id)
      : typeof product.id === "string"
        ? product.id
        : null;
  if (idAsString) {
    const normalized = normalizeUuid(idAsString);
    if (normalized) return normalized;
  }

  if (fallbackUuid) return normalizeUuid(fallbackUuid);
  return null;
}

export function getProductTitleFromRecord(product: JsonRecord): string {
  return toNonEmptyString(product.name) ?? toNonEmptyString(product.title) ?? "product";
}

export function getProductBrandSlugFromRecord(product: JsonRecord): string | null {
  const topLevelBrandSlug = toNonEmptyString(
    typeof product.brand_slug === "string" ? product.brand_slug : null
  );
  if (topLevelBrandSlug) {
    return topLevelBrandSlug;
  }

  const enrichment = product.enrichment;
  if (!enrichment || typeof enrichment !== "object") {
    return null;
  }

  const canonicalBrand =
    "canonical_brand" in enrichment && enrichment.canonical_brand
      ? enrichment.canonical_brand
      : null;
  if (!canonicalBrand || typeof canonicalBrand !== "object") {
    return null;
  }

  const canonicalBrandRecord = canonicalBrand as Record<string, unknown>;
  return toNonEmptyString(
    typeof canonicalBrandRecord.brand_slug === "string" ? canonicalBrandRecord.brand_slug : null
  );
}

export function buildCanonicalProductPathFromRecord(
  product: JsonRecord,
  fallbackUuid: string
): string | null {
  // The canonical id is the product's /v1 uuid, the same id carts and search use.
  const uuid = getProductUuidFromRecord(product, fallbackUuid);
  if (!uuid) return null;
  const title = getProductTitleFromRecord(product);
  const brand =
    getProductBrandLabel(product as Parameters<typeof getProductBrandLabel>[0]) || "brand";
  const brandSlug = getProductBrandSlugFromRecord(product);
  return buildCanonicalProductPath({ brand, brandSlug, title, uuid });
}

export function warnMissingCanonicalProductPath(context: string, productId: string): void {
  console.warn(`[${context}] Unable to build canonical product path for product "${productId}"`);
}
